import { giphyDarkestGrey } from '@giphy/colors'
import { Fragment, useContext, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { BORDER_RADIUS_MEDIUM, BORDER_RADIUS_SMALL } from '../../../../constants'
import CreationToolFileType from '../../../../types/file-type'
import CreationToolOutputType from '../../../../types/output-type'
import CreationToolStatus from '../../../../types/status'
import { validateSourceByOutput } from '../../../../utils/validate-source'
import ErrorModal from '../../../error-modal'
import EditMenu from '../../edit-menu'
import { CreationToolUploadContext } from '../../manager'
import { CreationToolUploadSource } from '../../types'
import Fields from './fields'
import Header from './header'
import PreviewComponent from './preview'

const Container = styled.div<{ isDim: boolean }>`
    background: ${giphyDarkestGrey};
    border-radius: ${BORDER_RADIUS_SMALL}px;
    margin-bottom: 5px;
    min-height: 50px;
    position: relative;
    ${(props) =>
        props.isDim &&
        css`
            opacity: 0.5;
            pointer-events: none;
        `}
`

const Error = styled(ErrorModal)`
    background: rgba(0, 0, 0, 0.9);
`

const Preview = styled(PreviewComponent)`
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    margin-top: 5px;
`

type Props = {
    source: CreationToolUploadSource
    isPreviewingByDefault?: boolean
    className?: string
}

const CreationUploadItem = ({ source, isPreviewingByDefault = false, className }: Props) => {
    const { source: src } = source
    const {
        status: uploadStatus,
        uploadSources,
        removeUploadSource,
        updateUploadSource,
        editing,
    } = useContext(CreationToolUploadContext)
    const [isPreviewing, setIsPreviewing] = useState<boolean>(isPreviewingByDefault)
    const { error, status, id: sourceId, uploadedId } = source
    const isUploading = uploadStatus !== CreationToolStatus.Idle
    const isBulk = uploadSources.length > 1
    const isEditable = CreationToolOutputType.editable(source.outputType)

    useEffect(() => {
        if (uploadStatus !== CreationToolStatus.Idle) {
            setIsPreviewing(false)
        }
    }, [uploadStatus, setIsPreviewing])

    const onChangeOutputType = async (outputType: CreationToolOutputType) => {
        const validatedFile = await validateSourceByOutput(src.source, outputType)
        updateUploadSource({
            ...source,
            source: validatedFile,
            error: undefined,
            outputType,
        })
    }

    const onRetry = () => {
        updateUploadSource({
            ...source,
            error: undefined,
        })
    }

    if (source.source.type === CreationToolFileType.UNVALIDATED_FILE && !error) {
        // Until the files are fully validated, do not render the components
        // but do render if they have an error
        return <Fragment />
    }

    return (
        <Container isDim={isUploading && status === CreationToolStatus.Idle && !uploadedId} className={className}>
            <Header
                {...{
                    source,
                    isBulk,
                    isPreviewing,
                    isUploading,
                    setIsPreviewing,
                }}
            />
            {isPreviewing && !editing && (
                <>
                    <Preview source={source}>
                        {error ? (
                            <Error
                                {...{
                                    error,
                                    sourceId: src.id,
                                    stickerDisplayMode: 'small',
                                    goBackLabel: `Remove`,
                                    onGoBack: () => removeUploadSource(sourceId),
                                    onChangeOutputType,
                                    onRetry,
                                }}
                            />
                        ) : (
                            isEditable && <EditMenu source={source} setIsPreviewing={setIsPreviewing} />
                        )}
                    </Preview>
                    {!error && (
                        <Fields
                            {...{
                                source,
                                isBulk,
                            }}
                        />
                    )}
                </>
            )}
        </Container>
    )
}

export default CreationUploadItem
